// Admin Trivia Page Component
// Provides admin interface for trivia question management
// Follows the Section, Component & Manager System architecture

import React, { useState, useEffect, useCallback } from 'react';
import { RevoltButton } from '../../../../components/RevoltButton';
import { SearchBar, TriviaItem } from '../../components';
import { TriviaModal } from './TriviaModal';
import { TriviaDeleteConfirmModal } from '../../../workspace/components/TriviaDeleteConfirmModal';
import './AdminContentTabs.css';
import './AdminQAPage.css';
import './TriviaAdminPage.css';

export function TriviaAdminPage() {
  const [triviaItems, setTriviaItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [selectedTrivia, setSelectedTrivia] = useState(null);

  // Delete confirmation modal state
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [triviaToDelete, setTriviaToDelete] = useState(null);
  const [isDeletingTrivia, setIsDeletingTrivia] = useState(false);

  // Load trivia items
  const loadTriviaItems = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch('/api/learn/admin/trivia', {
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Failed to load trivia questions');
      }

      const data = await response.json();
      setTriviaItems(data.trivia || []);
    } catch (error) {
      console.error('Failed to load trivia questions:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Load data on mount
  useEffect(() => {
    loadTriviaItems();
  }, [loadTriviaItems]);

  // Modal handlers
  const handleCreateTrivia = () => {
    setModalMode('create');
    setSelectedTrivia(null);
    setIsModalOpen(true);
  };

  const handleEditTrivia = (trivia) => {
    setModalMode('edit');
    setSelectedTrivia(trivia);
    setIsModalOpen(true);
  };

  const handleModalSubmit = async (triviaData) => {
    try {
      let response;
      let updatedTrivia;

      if (modalMode === 'create') {
        response = await fetch('/api/learn/admin/trivia', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          credentials: 'include',
          body: JSON.stringify(triviaData)
        });
      } else {
        response = await fetch(`/api/learn/admin/trivia/${selectedTrivia.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json'
          },
          credentials: 'include',
          body: JSON.stringify(triviaData)
        });
      }

      if (!response.ok) {
        throw new Error(`Failed to ${modalMode} trivia question`);
      }

      const data = await response.json();
      updatedTrivia = data.trivia;

      // Update local state
      if (modalMode === 'create') {
        setTriviaItems([updatedTrivia, ...triviaItems]);
      } else {
        setTriviaItems(triviaItems.map(trivia => trivia.id === updatedTrivia.id ? updatedTrivia : trivia));
      }
    } catch (error) {
      console.error(`Failed to ${modalMode} trivia:`, error);
      throw error; // Re-throw to let modal handle it
    }
  };

  // Handle delete trivia
  const handleDeleteTrivia = (trivia) => {
    setTriviaToDelete(trivia);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmTriviaDelete = async () => {
    if (!triviaToDelete) return;

    setIsDeletingTrivia(true);
    try {
      const response = await fetch(`/api/learn/admin/trivia/${triviaToDelete.id}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Failed to delete trivia question');
      }

      // Remove from local state
      setTriviaItems(triviaItems.filter(trivia => trivia.id !== triviaToDelete.id));
      setIsDeleteModalOpen(false);
      setTriviaToDelete(null);

    } catch (error) {
      console.error('Failed to delete trivia question:', error);
      alert('Failed to delete trivia question. Please try again.');
      setIsDeleteModalOpen(false);
      setTriviaToDelete(null);
    } finally {
      setIsDeletingTrivia(false);
    }
  };

  // Filter trivia items based on search
  const filteredTriviaItems = triviaItems.filter(trivia => {
    if (!searchTerm) return true;

    const search = searchTerm.trim();
    const searchLower = search.toLowerCase();

    // Check if this is a tag search (starts with #)
    if (search.startsWith('#')) {
      const tagSearch = searchLower.substring(1); // Remove the #
      return trivia.tags && trivia.tags.some(tag => tag.toLowerCase().includes(tagSearch));
    }

    // Regular search: search question text
    return trivia.question.toLowerCase().includes(searchLower);
  });

  if (loading) {
    return (
      <div className="admin-loading">
        <i className="fa-solid fa-brain admin-loading-icon"></i>
        <p>Loading trivia questions...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-error">
        <i className="fa-solid fa-exclamation-triangle"></i>
        <h3>Error loading trivia questions</h3>
        <p>{error}</p>
        <RevoltButton onClick={loadTriviaItems}>
          Try Again
        </RevoltButton>
      </div>
    );
  }

  return (
    <div className="admin-tab-content">
      <div className="admin-section-header admin-section-header--with-button">
        <div className="admin-section-header-left">
          <h2>Trivia Management</h2>
          <p>Manage trivia questions and categories.</p>
        </div>
        <div className="admin-section-header-right">
                <RevoltButton
                  variant="primary"
                  size="medium"
                  onClick={handleCreateTrivia}
                >
                  <i className="fa-solid fa-plus"></i>
                  Create Trivia Question
                </RevoltButton>
        </div>
      </div>

      {/* Search Bar */}
      <div className="admin-search-section">
        <SearchBar
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          placeholder="Search trivia questions..."
        />
      </div>

      {/* Trivia List */}
      {filteredTriviaItems.length === 0 ? (
        <div className="learn-empty-state">
          <i className="fa-solid fa-brain"></i>
          <h3>No trivia questions found</h3>
          <p>Try adjusting your search terms or create your first trivia question.</p>
        </div>
      ) : (
        <div className="admin-items-list">
          {filteredTriviaItems.map((trivia) => (
            <TriviaItem
              key={trivia.id}
              trivia={trivia}
              onEdit={handleEditTrivia}
              onDelete={handleDeleteTrivia}
            />
          ))}
        </div>
      )}

      {/* Trivia Modal */}
      <TriviaModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        mode={modalMode}
        initialData={selectedTrivia}
        onSubmit={handleModalSubmit}
      />

      {/* Trivia Delete Confirmation Modal */}
      <TriviaDeleteConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          if (!isDeletingTrivia) {
            setIsDeleteModalOpen(false);
            setTriviaToDelete(null);
          }
        }}
        onConfirm={handleConfirmTriviaDelete}
        trivia={triviaToDelete}
        isLoading={isDeletingTrivia}
      />
    </div>
  );
}